import { Money } from '@app/features/scope-overview/model/money.model'
import { SecondPartyContact } from '@app/features/scope-overview/model/second-party-contact.model'
import { Company } from '@core/model/company.model'
import { Privilege } from '@core/model/enums/privilege.enum'
import { FolderIdentity } from '@core/model/folder-identity'
import { ScopeVersion } from '@core/model/scope-version'
import { User } from '@core/model/user.model'
import { Type } from 'class-transformer'
import { SecondParty } from './second-party.model'

export class FolderVersion {
  id!: number
  @Type(() => Date) createdTs!: Date
  @Type(() => Date) updatedTs!: Date
  @Type(() => User) createdBy!: User
  @Type(() => User) updatedBy!: User
  @Type(() => FolderIdentity) identity!: FolderIdentity
  @Type(() => SecondPartyContact) secondPartyContact!: SecondPartyContact
  @Type(() => SecondParty) secondParty!: SecondParty
  versionNumber!: number
  @Type(() => User) startDate!: Date
  @Type(() => User) endDate!: Date
  @Type(() => User) owner!: User
  @Type(() => ScopeVersion) scopeVersions!: ScopeVersion[]
  status!: FolderStatus
  name!: string
  description!: string
  budgets?: any[]
  retainedTeams: any
  userPrivilegeRestrictions?: any[]
  userAdditionalPrivileges?: any[]
  privilegeCategories?: string[]
  comments?: any[]
  @Type(() => Company) company!: Company
  budgetBalances?: any[]
  @Type(() => Money) totalValues?: Money[]

  isStateDraft() {
    return this.status === 'DRAFT'
  }

  isArchived() {
    return this.identity.archived
  }

  isTemplate() {
    return this.identity.isTemplate
  }

  isPrivilegeRestricted(privilege: Privilege) {
    return this.userPrivilegeRestrictions?.indexOf(privilege) != -1
  }

  hasPrivilege(privilege: Privilege, user: User) {
    const hasAdditional = this.hasAdditionalPrivilege(privilege)
    if (hasAdditional) {
      return true
    }

    const hasPrivilege = user.hasPrivilege(privilege)
    if (hasPrivilege) {
      return !this.isPrivilegeRestricted(privilege)
    }
    return false
  }

  hasAdditionalPrivilege(privilege: Privilege) {
    return this.userAdditionalPrivileges?.indexOf(privilege) != -1
  }

  hasEnoughBudget() {
    const _this = this
    let hasEnoughBudget = true
    this.budgets?.forEach((budget) => {
      const totalValue = _this.totalValues?.find((it) => it.currency == budget.budget.currency)
      if (totalValue != null && budget.budget.amount < totalValue.amount) {
        hasEnoughBudget = false
      }
    })
    return hasEnoughBudget
  }

  getStatusText(): string {
    const statusText = this.status ? this.status.toString().replace(/_/, ' ') : ''
    return statusText.charAt(0).toUpperCase() + statusText.toLowerCase().slice(1)
  }
}

export enum FolderStatus {
  DRAFT = 'DRAFT',
  IN_REVIEW = 'IN_REVIEW',
  SCOPING = 'SCOPING',
}

export type FolderStatusName = Record<FolderStatus, string>

export const folderStatuses: FolderStatusName = {
  [FolderStatus.DRAFT]: FolderStatus.DRAFT,
  [FolderStatus.IN_REVIEW]: FolderStatus.IN_REVIEW,
  [FolderStatus.SCOPING]: FolderStatus.SCOPING,
}

export const folderStatusColor: FolderStatusName = {
  [FolderStatus.DRAFT]: 'text-slate-600',
  [FolderStatus.IN_REVIEW]: 'text-yellow-400',
  [FolderStatus.SCOPING]: 'text-green-500',
}
